import React, { useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Avatar,
  Tooltip,
} from "@mui/material";
import { QK } from "../../../base/qk";
import { getClientDayTimeline } from "../../../api/services/TimeLine/timeLine";

const ClientDayTimeline = ({ selectedDate, locationId }) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const { data: timelineResponse, isLoading, isFetching } = useQuery(
    [QK.clientsDayTimeline, selectedDate, locationId],
    () => getClientDayTimeline(selectedDate, locationId || "all"),
    { 
      enabled: !!selectedDate, 
      refetchOnWindowFocus: false,
      keepPreviousData: true, // Blinking বন্ধ করার জন্য
      select: (res) => res?.data || res 
    }
  );

  // নিরাপদ substring ফাংশন
  const formatTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return "00:00";
    return timeStr.substring(0, 5);
  };

  const getPercentagePos = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return 0;
    const parts = timeStr.split(':');
    if (parts.length < 2) return 0;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    return ((h * 60 + m) / (24 * 60)) * 100;
  };

  // প্রথমবার লোড হওয়ার সময় লোডার দেখাবে
  if (isLoading && !timelineResponse) {
    return (
      <Box sx={{ height: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={30} thickness={4} />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: "calc(100vh - 140px)",
        borderRadius: "8px",
        bgcolor: "#ffffff",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        opacity: isFetching ? 0.7 : 1, // ফেচিং এর সময় হালকা আবছা হবে
        transition: 'opacity 0.2s'
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box sx={{ minWidth: 1200, position: "relative" }}>
          
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: 100,
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #cbd5e1",
            }}
          >
            <Box
              sx={{
                width: 150,
                px: 1.5,
                py: 1,
                position: "sticky",
                left: 0,
                zIndex: 110,
                bgcolor: "#f8fafc",
                borderRight: "1px solid #cbd5e1",
              }}
            >
              <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#64748b" }}>
                SERVICE USER
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flex: 1 }}>
              {hours.map((h) => (
                <Box
                  key={h}
                  sx={{
                    flex: 1,
                    py: 1,
                    textAlign: "center",
                    borderRight: "1px solid #e2e8f0",
                  }}
                >
                  <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#0f172a" }}>
                    {h.toString().padStart(2, '0')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ROWS */}
          {timelineResponse?.map((client, idx) => {
            const isHovered = hoveredRow === client.client_id;
            
            return (
              <Box
                key={client.client_id || idx}
                onMouseEnter={() => setHoveredRow(client.client_id)}
                onMouseLeave={() => setHoveredRow(null)}
                sx={{
                  display: "flex",
                  minHeight: 50,
                  borderBottom: "1px solid #f1f5f9",
                  bgcolor: isHovered ? "#f8fafc" : "transparent",
                  position: 'relative',
                  // Z-index fix: ওপরের রো যেন নিচের রো এর ওপর টুলটিপ দেখাতে পারে
                  zIndex: (timelineResponse.length - idx),
                }}
              >
                {/* LEFT CELL */}
                <Box
                  sx={{
                    width: 150,
                    px: 1.5,
                    position: "sticky",
                    left: 0,
                    zIndex: 50,
                    bgcolor: isHovered ? "#f8fafc" : "white",
                    borderRight: "1px solid #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Avatar sx={{ width: 24, height: 24, fontSize: "10px", bgcolor: "#3182ce", fontWeight: 700 }}>
                    {client.client_name ? client.client_name[0] : "?"}
                  </Avatar>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#1e293b",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {client.client_name || "Unknown"}
                  </Typography>
                </Box>

                {/* TIMELINE AREA */}
                <Box sx={{ flex: 1, display: "flex", position: "relative" }}>
                  {hours.map((h) => (
                    <Box key={h} sx={{ flex: 1, borderRight: "1px solid #f8fafc" }} />
                  ))}

                  {client.tasks?.map((task) => {
                    const startPct = getPercentagePos(task.scheduled_start);
                    const endPct = getPercentagePos(task.scheduled_end);
                    const widthPct = Math.max(endPct - startPct, 4); 
                    const color = task.display_color || "#6366f1";

                    return (
                      <Tooltip
                        key={task.id}
                        arrow
                        disableInteractive={false}
                        PopperProps={{ sx: { zIndex: 9999 } }}
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>{task.visit_name}</Typography>
                            <Typography sx={{ fontSize: '10px' }}>
                              {formatTime(task.scheduled_start)} - {formatTime(task.scheduled_end)}
                            </Typography>
                            <Typography sx={{ fontSize: '10px' }}>Staff: {task.assigned_staff || 'N/A'}</Typography>
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: `${startPct}%`,
                            width: `${widthPct}%`,
                            top: "50%",
                            transform: "translateY(-50%)",
                            height: 28,
                            px: 0.5,
                            borderRadius: "4px",
                            backgroundColor: `${color}20`,
                            borderLeft: `4px solid ${color}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            zIndex: 10,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            "&:hover": { 
                                backgroundColor: `${color}35`, 
                                zIndex: 20,
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)' 
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "9px",
                              fontWeight: 900,
                              color: "#1e293b",
                              whiteSpace: "nowrap",
                              overflow: 'hidden'
                            }}
                          >
                            {formatTime(task.scheduled_start)}-{formatTime(task.scheduled_end)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default ClientDayTimeline;